import * as React from "react";

import { Button, Field, MessageBar, MessageBarBody, makeStyles, tokens } from "@fluentui/react-components";

import type { MatterListItem } from "@features/matter";
import { MatterCombobox, MatterError, getMatters } from "@features/matter";
import { ItemContextCard, UnsupportedAttachmentError, getAttachmentBlob, useMailboxItem } from "@features/mailbox";

import { runWithConcurrency } from "@shared/lib/concurrency";

import AttachmentList from "./AttachmentList";
import type { AttachmentRowData } from "./models";
import { isSupportedAttachment } from "./supportedAttachments";
import { DocumentError, uploadMatterDocument } from "./documentApi";

const UPLOAD_CONCURRENCY = 5;

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalL,
  },
  matterField: {
    padding: `0 ${tokens.spacingHorizontalL}`,
  },
  footer: {
    padding: `0 ${tokens.spacingHorizontalL}`,
  },
});

const AttachmentUploader: React.FC = () => {
  const styles = useStyles();
  const { attachments } = useMailboxItem();

  const [matters, setMatters] = React.useState<MatterListItem[]>([]);
  const [mattersLoading, setMattersLoading] = React.useState(true);
  const [mattersError, setMattersError] = React.useState<string | null>(null);
  const [defaultMatterId, setDefaultMatterId] = React.useState<number | null>(null);

  const [rows, setRows] = React.useState<AttachmentRowData[]>([]);
  const [isUploading, setIsUploading] = React.useState(false);

  React.useEffect(() => {
    setRows(
      attachments.map((attachment) => {
        const supported = isSupportedAttachment(attachment.name);
        return {
          id: attachment.id,
          originalName: attachment.name,
          size: attachment.size,
          selected: supported,
          customName: attachment.name,
          matterOverrideId: null,
          status: supported ? "idle" : "unsupported",
        };
      })
    );
  }, [attachments]);

  React.useEffect(() => {
    let cancelled = false;

    (async () => {
      setMattersLoading(true);
      setMattersError(null);

      try {
        const list = await getMatters();
        if (!cancelled) {
          setMatters(list);
        }
      } catch (error) {
        if (!cancelled) {
          setMattersError(error instanceof MatterError ? error.message : "Couldn't load matters");
        }
      } finally {
        if (!cancelled) {
          setMattersLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const updateRow = (id: string, patch: Partial<AttachmentRowData>) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const toggleRow = (id: string, selected: boolean) => {
    updateRow(id, { selected });
  };

  const toggleAll = (selected: boolean) => {
    // Rows already uploaded, or of an unsupported type, stay excluded — nothing to select them for.
    setRows((prev) =>
      prev.map((row) => (row.status === "success" || row.status === "unsupported" ? row : { ...row, selected }))
    );
  };

  const renameRow = (id: string, customName: string) => {
    updateRow(id, { customName });
  };

  const overrideRowMatter = (id: string, matterOverrideId: number | null) => {
    updateRow(id, { matterOverrideId });
  };

  const runJob = async (row: AttachmentRowData) => {
    const item = Office.context.mailbox.item as Office.MessageRead | undefined;
    const matterId = row.matterOverrideId ?? defaultMatterId;

    if (!item || matterId === null) {
      updateRow(row.id, { status: "error", errorMessage: "The message is no longer open" });
      return;
    }

    updateRow(row.id, { status: "uploading", errorMessage: undefined });

    try {
      const attachment = attachments.find((a) => a.id === row.id);
      const blob = await getAttachmentBlob(item, row.id, attachment?.contentType ?? "");

      await uploadMatterDocument(matterId, row.customName, blob);

      updateRow(row.id, { status: "success", selected: false });
    } catch (error) {
      const message =
        error instanceof UnsupportedAttachmentError
          ? "Cloud or .eml attachments aren't supported yet"
          : error instanceof DocumentError
            ? error.message
            : "Couldn't upload the file";
      updateRow(row.id, { status: "error", errorMessage: message });
    }
  };

  const selectedRows = rows.filter((row) => row.selected);
  const canUpload = defaultMatterId !== null && selectedRows.length > 0 && !isUploading;

  const handleUpload = async () => {
    if (!canUpload) {
      return;
    }

    setIsUploading(true);
    await runWithConcurrency(selectedRows, UPLOAD_CONCURRENCY, runJob);
    setIsUploading(false);
  };

  const handleRetry = async (id: string) => {
    if (isUploading) {
      return;
    }

    const row = rows.find((r) => r.id === id);
    if (!row) {
      return;
    }

    setIsUploading(true);
    await runJob(row);
    setIsUploading(false);
  };

  return (
    <div className={styles.root}>
      <ItemContextCard />

      <Field className={styles.matterField} label="Default matter">
        {mattersError ? (
          <MessageBar intent="error">
            <MessageBarBody>{mattersError}</MessageBarBody>
          </MessageBar>
        ) : (
          <MatterCombobox
            matters={matters}
            loading={mattersLoading}
            selectedMatterId={defaultMatterId}
            onSelect={setDefaultMatterId}
            placeholder="Select a matter"
            disabled={isUploading}
          />
        )}
      </Field>

      <AttachmentList
        rows={rows}
        matters={matters}
        mattersLoading={mattersLoading}
        disabled={isUploading}
        onToggleAll={toggleAll}
        onToggle={toggleRow}
        onNameChange={renameRow}
        onMatterOverrideChange={overrideRowMatter}
        onRetry={handleRetry}
      />

      {rows.length > 0 && (
        <div className={styles.footer}>
          <Button appearance="primary" disabled={!canUpload} onClick={handleUpload}>
            {isUploading ? "Uploading..." : `Upload (${selectedRows.length})`}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AttachmentUploader;
